import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { useAuthContext } from 'Shared/Security/Domain/Contexts/authContext';
import { Microsoft, Email, Lock, Visibility, VisibilityOff, ArrowForward } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import DialogLoader from 'UI/Application/Components/Loaders/DialogLoader';
import ForgotPasswordDialog from 'Shared/Security/Application/Components/ForgotPasswordDialog';

function LoginView() {
  const { t } = useTranslation();
  const { login, isAuthenticated, loginWithToken } = useAuthContext();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loginLocked, setLoginLocked] = useState(false);
  const [doLoginWithTokenCheck, setDoLoginWithToken] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);

  const handleInputEmailChange = (event) => {
    setEmail(event.target.value);
  };

  const handleInputPasswordChange = (event) => {
    setPassword(event.target.value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loginLocked) {
      return;
    }
    setLoginLocked(true);
    await login(email, password);
    if (!isAuthenticated) {
      setError(t('security.login.error'));
    }
    setLoginLocked(false);
  };

  const handleTokenIsOnURL = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const tartgetPath = urlParams.get('targetPath');
    if (token) {
      setLoginLocked(true);
      await loginWithToken(token, tartgetPath);
      if (!isAuthenticated) {
        setError(t('security.token.error'));
      }
      setLoginLocked(false);
    }
    setDoLoginWithToken(false);
  };

  useEffect(() => {
    if (!isAuthenticated && doLoginWithTokenCheck) {
      handleTokenIsOnURL();
    }
  }, [isAuthenticated, doLoginWithTokenCheck]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `radial-gradient(1200px 600px at -10% -20%, rgba(25,118,210,0.15), transparent 60%),
                     radial-gradient(1000px 500px at 110% 120%, rgba(0,172,193,0.12), transparent 60%),
                     linear-gradient(135deg, #f6f9fc 0%, #eef5ff 100%)`,
        px: 2,
        py: { xs: 4, md: 6 }
      }}
    >
      <Container maxWidth="md" disableGutters>
        <Paper elevation={8} sx={{ overflow: 'hidden', borderRadius: 3 }}>
          <Grid container>
            {/* Hero lateral en pantallas medianas/grandes */}
            <Grid item xs={12} md={6}
              sx={{
                display: { xs: 'none', md: 'flex' },
                flexDirection: 'column',
                justifyContent: 'space-between',
                color: '#fff',
                background: 'linear-gradient(160deg, #1976d2 0%, #00acc1 100%)',
                p: 4,
                position: 'relative'
              }}
            >
              <Box>
                <img src="/img/logo.png" alt="logo" style={{ maxWidth: 160, filter: 'brightness(0) invert(1)' }} />
                <Typography variant="h4" fontWeight={700} mt={4} lineHeight={1.2}>
                  {t('security.login.login')}
                </Typography>
                <Typography variant="body1" mt={2} sx={{ opacity: 0.9 }}>
                  Elevando tu productividad: accede a la intranet para gestionar proyectos, incidencias y documentación.
                </Typography>
              </Box>
              {/* Motivo decorativo vertical evocando ascensor */}
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, mt: 6 }}>
                <Box sx={{ height: 12, width: '8%', bgcolor: 'rgba(255,255,255,0.9)', borderRadius: 1 }} />
                <Box sx={{ height: 24, width: '10%', bgcolor: 'rgba(255,255,255,0.8)', borderRadius: 1 }} />
                <Box sx={{ height: 48, width: '12%', bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }} />
                <Box sx={{ height: 72, width: '14%', bgcolor: 'rgba(255,255,255,0.6)', borderRadius: 1 }} />
                <Box sx={{ height: 96, width: '16%', bgcolor: 'rgba(255,255,255,0.5)', borderRadius: 1 }} />
              </Box>
            </Grid>

            {/* Panel de formulario */}
            <Grid item xs={12} md={6} sx={{ p: { xs: 3, sm: 4 } }}>
              {/* Logo para móviles */}
              <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 2 }}>
                <img src="/img/logo.png" alt="logo" style={{ maxWidth: 140 }} />
              </Box>

              <Typography variant="h5" fontWeight={700} textAlign={{ xs: 'center', md: 'left' }}>
                {t('security.login.login')}
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign={{ xs: 'center', md: 'left' }}>
                Accede con tu correo corporativo
              </Typography>

              <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label={t('security.login.email')}
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={handleInputEmailChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label={t('security.login.password')}
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={handleInputPasswordChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setForgotPasswordOpen(true)}
                    disabled={loginLocked}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.825rem',
                      px: 0.5,
                      color: 'text.secondary',
                      '&:hover': { color: 'primary.main', bgcolor: 'transparent' }
                    }}
                  >
                    {t('security.login.forgotPassword')}
                  </Button>
                </Box>

                {error && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                  </Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, py: 1.2 }}
                  disabled={loginLocked}
                  endIcon={<ArrowForward />}
                >
                  {t('security.login.login')}
                </Button>

                <Divider sx={{ my: 3 }}>{t('security.login.loginwith')}</Divider>

                <Button
                  href={`${process.env.REACT_APP_BACKEND_URL}/v1/public/oauth2/azure`}
                  onClick={() => setLoginLocked(true)}
                  fullWidth
                  variant="outlined"
                  color="primary"
                  sx={{ py: 1.1 }}
                  startIcon={<Microsoft />}
                  disabled={loginLocked}
                >
                  Entrar con Outlook
                </Button>

                <Button
                  href={`${process.env.REACT_APP_OLD_INTRANET_URL}/login/intranet-new`}
                  onClick={() => setLoginLocked(true)}
                  fullWidth
                  variant="text"
                  color="secondary"
                  sx={{ mt: 1, textDecoration: 'underline' }}
                  disabled={loginLocked}
                >
                  {t('security.login.loginOld')}
                </Button>
              </Box>

              <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mt: 4 }}>
                © {new Date().getFullYear()} — Intranet
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Container>
      <DialogLoader open={loginLocked} />
      <ForgotPasswordDialog
        open={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
        initialEmail={email}
      />
    </Box>
  );
}

export default LoginView;
